//聊天室
import { ViewMode } from "./types"
import React, { useEffect, useCallback, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import Clipboard from "~/ui/clipboard"
import styled, { useTheme } from "~/ui/styled"
import { useRoute } from "~router/navigation"
import useNavigation from "~hooks/useNavigation"
import { PopupOptionListModal } from "~modals/popup-option-list-modal"
import { ActionsSheet } from "~components/actions-sheet"
import Icon from "~widgets/icon"
import MessageInput from "~components/message-input/message-input"
import MessageList from "~components/message-list/message-list"
import { MessageData, MessageTypes } from "~proto"
import { useAudioRecorder } from "~context"
import { useStores } from "~models"
import useGroupActions from "~hooks/useGroupActions"
import { globalToast } from "~utils"
import ChatHeaderBar from "~components/chat/chat-header-bar"
import { useActiveChat } from "~hooks/useActiveChat"

const Container = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};
`

const TopBar = styled.div`
  flex-shrink: 0;
  width: 100%;
`

const Main = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ListArea = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
`

const IsBlackBarWrap = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 40px 10px 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  background-color: ${({ theme }) => theme.colors.background};
`

const IsBlackBarText = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`

const IsBlackBarCloseWrap = styled.button`
  position: absolute;
  right: 10px;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PinnedBarWrap = styled.button`
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  border: none;
  background-color: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 10px 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
`

const PinnedBarText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const PinnedBarTag = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-right: 8px;
  flex-shrink: 0;
`

const GoBottomButtonWrap = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 800;
  pointer-events: none;

  & > button {
    pointer-events: auto;
  }
`

const GoBottomButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 20px;
  width: 40px;
  height: 40px;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background-color: ${({ theme }) => theme.colors.primary};
`

const InputArea = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 100%;
  z-index: 900;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: env(safe-area-inset-bottom, 0px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`

const Chat: React.FC = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const route = useRoute<{ chat_id?: string; scrollToMessageId?: string | number }>()
  const audioRecorderPlayer = useAudioRecorder()
  const stores = useStores()
  const { pinGroupMessage } = useGroupActions()
  const { chatId, chat } = useActiveChat()
  const {
    chatStore: {
      rightClickMenuVisible,
      setRightClickMenuVisible,
      rightClickMenuSelectedMessage,
      setRightClickMenuMessageId,
      isReplyBarVisible,
      setIsReplyBarVisible,
    },
    userStore: { currentUser },
    messageStore: { senddeleteMessageByMessageID, repairChatMessageUsers },
    blackListStore,
  } = stores
  const listRef = useRef<any>(null)
  const [mode, setMode] = useState<ViewMode>(ViewMode.Default)
  const [isBlackBarVisible, setIsBlackBarVisible] = useState(false)
  const [actionsSheetVisible, setActionsSheetVisible] = useState(false)
  const [actionsSheetOptions, setActionsSheetOptions] = useState([])
  const [isScrollToBottom, setIsScrollToBottom] = useState(true)
  const [expandListToIndex, setExpandListToIndex] = useState<number | null>(null)
  const data = chat?.sortedMessages ?? []
  const isBlack =
    !!chat && !(chat.chatTypeEnum === "GROUP") && !!blackListStore.black_list.get(`${chat.chat_id}`)

  useEffect(() => {
    setExpandListToIndex(null)
    setRightClickMenuVisible(false)
    setRightClickMenuMessageId("")
    setIsReplyBarVisible(false)
  }, [chatId, setIsReplyBarVisible, setRightClickMenuMessageId, setRightClickMenuVisible])

  useEffect(() => {
    if (chat) repairChatMessageUsers(chat)
  }, [chat, repairChatMessageUsers])

  const setPopupOptionListModal = useCallback(({ visible, message }) => {
    setRightClickMenuVisible(visible)
    setRightClickMenuMessageId(message.message_id)
  }, [setRightClickMenuMessageId, setRightClickMenuVisible])

  const scrollToEnd = useCallback(() => {
    if (!listRef.current || data.length === 0) return
    requestAnimationFrame(() => {
      listRef.current?.scrollToIndex({ index: 0, animated: true })
    })
  }, [data.length])

  const matchMessageId = useCallback((message: MessageData, id: string | number) => {
    const target = String(id)
    return String(message.id) === target || String(message.message_id) === target
  }, [])

  const scrollById = useCallback(
    async (id: string | number) => {
      if (!chat) return
      let index = chat.sortedMessages.findIndex((el: MessageData) => matchMessageId(el, id))

      if (index < 0 && !chat.isNoMoreData) {
        let attempts = 0
        while (index < 0 && !chat.isNoMoreData && attempts < 30) {
          const oldest = chat.sortedMessages[chat.sortedMessages.length - 1]
          await chat.publicReadChatHistory(oldest?.id ?? null)
          index = chat.sortedMessages.findIndex((el: MessageData) => matchMessageId(el, id))
          attempts++
        }
      }

      if (index < 0) {
        globalToast.show("未找到该消息")
        return
      }

      setExpandListToIndex(index)
      setIsScrollToBottom(index === 0)
    },
    [chat, matchMessageId],
  )

  const scrollByIdRef = useRef(scrollById)
  scrollByIdRef.current = scrollById

  const handleScroll = useCallback((offsetY) => {
    setIsScrollToBottom(offsetY <= 0)
  }, [])

  const handleBeginDrag = useCallback(() => {
    setMode(ViewMode.ScrollView)
  }, [])

  const handleEndDrag = useCallback(() => {
    setMode(ViewMode.Default)
  }, [])

  const handleContainerPointerDown = useCallback((event: React.PointerEvent) => {
    const target = event.target as HTMLElement | null
    if (target?.closest("[data-chat-input]")) return
    setMode(ViewMode.ScrollView)
  }, [])

  const options = [
    {
      id: 1,
      visible: true,
      text: "回复",
      icon: {
        name: "huifu",
        color: theme.colors.text,
        size: 16,
      },
      onClick: () => {
        setRightClickMenuVisible(false)
        setIsReplyBarVisible(true)
      },
    },
    {
      id: 2,
      visible: rightClickMenuSelectedMessage?.type === MessageTypes.TEXT,
      text: "复制",
      icon: {
        name: "fuzhi",
        color: theme.colors.text,
        size: 16,
      },
      onClick: () => {
        setRightClickMenuVisible(false)
        Clipboard.setString(rightClickMenuSelectedMessage.content)
      },
    },
    {
      id: 3,
      visible: true,
      text: "转发",
      icon: {
        name: "zhuanfa",
        color: theme.colors.text,
        size: 16,
      },
      onClick: () => {
        setRightClickMenuVisible(false)
        navigation.navigate("ForwardMessage", { messageID: rightClickMenuSelectedMessage?.message_id })
      },
    },
    {
      id: 4,
      visible: true,
      text: "删除",
      icon: {
        name: "shanchu",
        color: theme.colors.text,
        size: 16,
      },
      onClick: () => {
        const deleteOptions = [
          {
            text: "为我删除",
            onClick: () => {
              setRightClickMenuVisible(false)
              setActionsSheetVisible(false)
              setActionsSheetOptions([])
              senddeleteMessageByMessageID(rightClickMenuSelectedMessage, true)
            },
          },
          {
            text: "取消",
            onClick: () => {
              setRightClickMenuVisible(false)
              setActionsSheetVisible(false)
              setActionsSheetOptions([])
            },
          },
        ]
        if (rightClickMenuSelectedMessage?.sender?.id === currentUser.id) {
          deleteOptions.unshift({
            text: "为双方删除",
            onClick: () => {
              setRightClickMenuVisible(false)
              setActionsSheetVisible(false)
              setActionsSheetOptions([])
              senddeleteMessageByMessageID(rightClickMenuSelectedMessage)
            },
          })
        }
        setActionsSheetOptions(deleteOptions)
        setActionsSheetVisible(true)
      },
    },
    {
      id: 5,
      visible:
        chat?.chatTypeEnum === "GROUP" &&
        (chat?.isCurrentUserIsAdmin || chat?.isCurrentUserIsManager) &&
        !!rightClickMenuSelectedMessage?.id &&
        !rightClickMenuSelectedMessage?.is_deleted &&
        !rightClickMenuSelectedMessage?.notify_type,
      text:
        chat?.pinned_message_id != null &&
        rightClickMenuSelectedMessage?.id === chat?.pinned_message_id
          ? "取消置顶"
          : "置顶",
      icon: {
        name: "zdtx",
        color: theme.colors.text,
        size: 16,
      },
      onClick: async () => {
        if (!chat?.chat_id) return
        try {
          setRightClickMenuVisible(false)
          const targetId =
            chat.pinned_message_id != null &&
            rightClickMenuSelectedMessage?.id === chat.pinned_message_id
              ? 0
              : rightClickMenuSelectedMessage?.id
          await pinGroupMessage({ chat_id: chat.chat_id, message_id: targetId })
        } catch {
          // server errors surfaced elsewhere
        }
      },
    },
  ].filter((o) => o.visible)

  useEffect(() => {
    return () => {
      audioRecorderPlayer.stopRecorder()
      audioRecorderPlayer.removeRecordBackListener()
    }
  }, [audioRecorderPlayer])

  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        scrollToEnd()
      }
    }
    document.addEventListener("focusin", onFocusIn)
    return () => document.removeEventListener("focusin", onFocusIn)
  }, [scrollToEnd])

  useEffect(() => {
    setIsBlackBarVisible(isBlack)
  }, [isBlack])

  useEffect(() => {
    setRightClickMenuVisible(false)
    setIsReplyBarVisible(false)
    setRightClickMenuMessageId("")
  }, [setIsReplyBarVisible, setRightClickMenuMessageId, setRightClickMenuVisible])

  useEffect(() => {
    const targetId = route?.params?.scrollToMessageId
    if (targetId == null || targetId === "") return undefined
    const t = setTimeout(() => {
      void scrollByIdRef.current(targetId)
    }, 150)
    return () => clearTimeout(t)
  }, [route?.params?.scrollToMessageId, chat?.chat_id, chatId])

  useEffect(() => {
    if (!rightClickMenuSelectedMessage) {
      setRightClickMenuVisible(false)
      setIsReplyBarVisible(false)
    }
  }, [rightClickMenuSelectedMessage, setIsReplyBarVisible, setRightClickMenuVisible])

  const inputProps = {
    listRef,
    viewMode: mode,
    setViewMode: setMode,
    popupOptionListModal: {
      visible: rightClickMenuVisible,
      message: rightClickMenuSelectedMessage,
    },
    isReplyBarVisible,
    setIsReplyBarVisible,
  }

  if (!chat || !chatId) return null

  return (
    <Container onPointerDown={handleContainerPointerDown}>
      <PopupOptionListModal
        visible={rightClickMenuVisible}
        setVisible={setRightClickMenuVisible}
        options={options}
      />
      <ActionsSheet
        visible={actionsSheetVisible}
        setVisible={setActionsSheetVisible}
        data={actionsSheetOptions}
      />
      <TopBar>
        <ChatHeaderBar />
        {isBlackBarVisible && (
          <IsBlackBarWrap>
            <IsBlackBarText>该用户在您的黑名单中</IsBlackBarText>
            <IsBlackBarCloseWrap
              type="button"
              aria-label="关闭"
              onClick={() => {
                blackListStore.publicRemoveBlacklist(chat.chat_id)
                setIsBlackBarVisible(false)
              }}
            >
              <Icon name="close" size={20} color={theme.colors.secondaryText} />
            </IsBlackBarCloseWrap>
          </IsBlackBarWrap>
        )}
        {!!chat.pinned_message_id && (
          <PinnedBarWrap
            type="button"
            onClick={() => {
              const targetId = chat.pinned_message_id
              const exist = data.some((m: MessageData) => matchMessageId(m, targetId))
              if (!exist) {
                globalToast.show("置顶消息不在当前列表，可下拉加载更多后重试")
                return
              }
              scrollById(targetId)
            }}
          >
            <PinnedBarTag>置顶</PinnedBarTag>
            <PinnedBarText>{chat.pinned_message_content || "点击查看置顶消息"}</PinnedBarText>
            <Icon name="right" size={16} color={theme.colors.secondaryText} />
          </PinnedBarWrap>
        )}
      </TopBar>
      <Main>
        <ListArea>
          <MessageList
            key={chatId}
            activeChatId={chatId}
            visibleContentPosition={chat.chatTypeEnum === "CHANNEL" && !chat.isCurrentUserIsAdmin}
            setMode={setMode}
            setPopupOptionListModal={setPopupOptionListModal}
            listRef={listRef}
            expandToIndex={expandListToIndex}
            scrollById={(id) => scrollById(id)}
            onScroll={handleScroll}
            onScrollEndDrag={handleEndDrag}
            onScrollBeginDrag={handleBeginDrag}
          />
          {!isScrollToBottom && (
            <GoBottomButtonWrap>
              <GoBottomButton type="button" aria-label="回到底部" onClick={scrollToEnd}>
                <Icon name="ztdx" size={20} color={theme.colors.blockBackground} />
              </GoBottomButton>
            </GoBottomButtonWrap>
          )}
        </ListArea>
        <InputArea data-chat-input>
          <MessageInput {...inputProps} />
        </InputArea>
      </Main>
    </Container>
  )
}

export default observer(Chat)
